import { useState } from 'react'
import { updateSupplier, deleteSupplier } from '../../../services/supplierService'
import { showAlert, showConfirm } from '../../../services/dialogService'
import TextareaWithCounter from '../../../components/TextareaWithCounter/TextareaWithCounter'
import './EditVendor.css'

const BUDGET_ITEMS = ['Wedding planner', 'Photographer', 'Videographer', 'Catering', 'Florist', 'DJ', 'Venue', 'Cake', 'Hair & Makeup', 'Transport', 'Invitations', 'Other']

function EditVendor({ supplier, onClose, onSave, onDelete }) {
  const [activeTab, setActiveTab] = useState('details')
  const [form, setForm] = useState({
    name: supplier.name || supplier.business_name || '',
    business_name: supplier.business_name || supplier.name || '',
    category: supplier.category || '',
    contact_name: supplier.contact_name || '',
    email: supplier.email || '',
    phone: supplier.phone || '',
    web: supplier.web || '',
    arrival_time: supplier.arrival_time || '',
    end_time: supplier.end_time || '',
    meal_required: supplier.meal_required || false,
    dietaries: supplier.dietaries || '',
    inclusions_notes: supplier.inclusions_notes || supplier.notes || '',
    total_cost_package: supplier.total_cost_package || supplier.rate || '',
    budget_item: supplier.budget_item || '',
    initial_payment: supplier.initial_payment || '',
    initial_due_date: supplier.initial_due_date ? supplier.initial_due_date.split('T')[0] : '',
    initial_paid: supplier.initial_paid || false,
    second_payment: supplier.second_payment || '',
    second_due_date: supplier.second_due_date ? supplier.second_due_date.split('T')[0] : '',
    second_paid: supplier.second_paid || false,
  })
  const [saving, setSaving] = useState(false)

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const updates = {
        name: form.name || form.business_name,
        business_name: form.business_name || form.name,
        category: form.category,
        contact_name: form.contact_name || null,
        email: form.email || null,
        phone: form.phone || null,
        web: form.web || null,
        arrival_time: form.arrival_time || null,
        end_time: form.end_time || null,
        meal_required: !!form.meal_required,
        dietaries: form.dietaries || null,
        inclusions_notes: form.inclusions_notes || null,
        total_cost_package: form.total_cost_package ? parseFloat(form.total_cost_package) : null,
        budget_item: form.budget_item || null,
        initial_payment: form.initial_payment ? parseFloat(form.initial_payment) : null,
        initial_due_date: form.initial_due_date || null,
        initial_paid: !!form.initial_paid,
        second_payment: form.second_payment ? parseFloat(form.second_payment) : null,
        second_due_date: form.second_due_date || null,
        second_paid: !!form.second_paid,
      }
      const updated = await updateSupplier(supplier.id, updates)
      onSave(updated)
    } catch (err) {
      console.error(err)
      await showAlert('Error saving supplier')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    const ok = await showConfirm('Delete this vendor permanently?', { title: 'Delete vendor', confirmLabel: 'Delete', cancelLabel: 'Cancel', variant: 'danger' })
    if (!ok) return
    try {
      await deleteSupplier(supplier.id)
      onDelete()
    } catch (err) {
      console.error(err)
      await showAlert('Error deleting vendor')
    }
  }

  const tabs = [
    { id: 'details', label: 'Details' },
    { id: 'contacts', label: 'Contacts' },
    { id: 'payments', label: 'Payments' },
    { id: 'documents', label: 'Documents' },
  ]

  return (
    <div className="edit-vendor-panel">
      <div className="edit-vendor-header">
        <h3>Edit vendor</h3>
        <div className="edit-vendor-header-actions">
          <button className="edit-vendor-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="edit-vendor-btn-add" onClick={() => showAlert('Add vendor - navigate to Numbers please')}>Add vendor</button>
        </div>
      </div>

      <div className="edit-vendor-name">{form.name || form.business_name || 'Untitled'}</div>

      <div className="edit-vendor-tabs">
        {tabs.map(t => (
          <button
            key={t.id}
            className={`edit-vendor-tab ${activeTab === t.id ? 'active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="edit-vendor-content">
        {activeTab === 'details' && (
          <div className="edit-vendor-section">
            <div className="edit-vendor-field">
              <label>Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Business name"
              />
            </div>
            <div className="edit-vendor-field">
              <label>Category / Vendor type</label>
              <input
                type="text"
                value={form.category}
                onChange={(e) => handleChange('category', e.target.value)}
                placeholder="e.g. Photographer"
              />
            </div>
            <div className="edit-vendor-field">
              <label>Arrival Time</label>
              <input
                type="text"
                value={form.arrival_time}
                onChange={(e) => handleChange('arrival_time', e.target.value)}
                placeholder="e.g. 10:00"
              />
            </div>
            <div className="edit-vendor-field">
              <label>End Time</label>
              <input
                type="text"
                value={form.end_time}
                onChange={(e) => handleChange('end_time', e.target.value)}
                placeholder="e.g. 18:00"
              />
            </div>
            <div className="edit-vendor-field">
              <label>Meal Required</label>
              <input
                type="checkbox"
                checked={form.meal_required}
                onChange={(e) => handleChange('meal_required', e.target.checked)}
              />
            </div>
            <div className="edit-vendor-field">
              <label>Dietaries</label>
              <input
                type="text"
                value={form.dietaries}
                onChange={(e) => handleChange('dietaries', e.target.value)}
                placeholder="e.g. Vegetarian"
              />
            </div>
            <div className="edit-vendor-field">
              <label>Inclusions / Notes</label>
              <TextareaWithCounter
                value={form.inclusions_notes}
                onChange={(e) => handleChange('inclusions_notes', e.target.value)}
                rows={4}
                maxLength={500}
              />
            </div>
          </div>
        )}

        {activeTab === 'contacts' && (
          <div className="edit-vendor-section">
            <div className="edit-vendor-field">
              <label>Contact Name</label>
              <input
                type="text"
                value={form.contact_name}
                onChange={(e) => handleChange('contact_name', e.target.value)}
              />
            </div>
            <div className="edit-vendor-field">
              <label>Phone</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
              />
            </div>
            <div className="edit-vendor-field">
              <label>Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
              />
            </div>
            <div className="edit-vendor-field">
              <label>Web</label>
              <input
                type="url"
                value={form.web}
                onChange={(e) => handleChange('web', e.target.value)}
                placeholder="https://..."
              />
            </div>
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="edit-vendor-section">
            <div className="edit-vendor-field">
              <label>Total Cost Package</label>
              <input
                type="number"
                step="0.01"
                value={form.total_cost_package}
                onChange={(e) => handleChange('total_cost_package', e.target.value)}
              />
            </div>
            {(() => {
              const total = parseFloat(form.total_cost_package) || 0
              const paid = (form.initial_paid ? (parseFloat(form.initial_payment) || 0) : 0) + (form.second_paid ? (parseFloat(form.second_payment) || 0) : 0)
              const remaining = Math.max(0, total - paid)
              return (
                <div className="edit-vendor-payment-summary">
                  <p><strong>Paid so far:</strong> ${paid.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                  <p><strong>Remaining to pay:</strong> ${remaining.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                </div>
              )
            })()}
            <div className="edit-vendor-field">
              <label>Budget item</label>
              <select
                value={form.budget_item}
                onChange={(e) => handleChange('budget_item', e.target.value)}
              >
                <option value="">—</option>
                {BUDGET_ITEMS.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div className="edit-vendor-payment-group">
              <h4>First payment</h4>
              <div className="edit-vendor-field">
                <label>Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.initial_payment}
                  onChange={(e) => handleChange('initial_payment', e.target.value)}
                />
              </div>
              <div className="edit-vendor-field">
                <label>Due on</label>
                <input
                  type="date"
                  value={form.initial_due_date}
                  onChange={(e) => handleChange('initial_due_date', e.target.value)}
                />
              </div>
              <div className="edit-vendor-field">
                <label>
                  <input
                    type="checkbox"
                    checked={form.initial_paid}
                    onChange={(e) => handleChange('initial_paid', e.target.checked)}
                  />
                  Paid
                </label>
              </div>
            </div>
            <div className="edit-vendor-payment-group">
              <h4>Second payment</h4>
              <div className="edit-vendor-field">
                <label>Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.second_payment}
                  onChange={(e) => handleChange('second_payment', e.target.value)}
                />
              </div>
              <div className="edit-vendor-field">
                <label>Due on</label>
                <input
                  type="date"
                  value={form.second_due_date}
                  onChange={(e) => handleChange('second_due_date', e.target.value)}
                />
              </div>
              <div className="edit-vendor-field">
                <label>
                  <input
                    type="checkbox"
                    checked={form.second_paid}
                    onChange={(e) => handleChange('second_paid', e.target.checked)}
                  />
                  Paid
                </label>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="edit-vendor-section">
            <p className="edit-vendor-placeholder">Documents section — coming soon. You can upload contracts and files here.</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default EditVendor
